<template>
  <div class="order-versions-container">
    <div class="header-section">
      <h2>Order Version History</h2>
      <div v-if="orderNumber" class="order-info">
        <span class="label">Order Number:</span>
        <span class="value">{{ orderNumber }}</span>
      </div>
      <button
        @click="goToOrder"
        class="btn btn-primary btn-sm"
      >
        <i class="fa fa-edit" aria-hidden="true"></i> Edit Order
      </button>
      <DxButton
        text="Back to Search"
        icon="back"
        @click="goBack"
        class="back-button"
      />
    </div>

    <DxDataGrid
      :data-source="versions"
      :show-borders="true"
      :row-alternation-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :column-min-width="50"
      class="versions-grid"
      @cell-prepared="onCellPrepared"
    >
      <DxScrolling mode="standard" />
      <DxPaging :page-size="50" />
      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[20, 50, 100]"
        :show-info="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" />

      <!-- Actions Column -->
      <DxColumn
        caption="Actions"
        :width="100"
        :allow-filtering="false"
        :allow-sorting="false"
        cell-template="actions-template"
      />

      <!-- Version Info Columns -->
      <DxColumn
        data-field="VersionNumber"
        caption="Version"
        :width="80"
        :allow-filtering="false"
        cell-template="version-template"
      />
      <DxColumn
        data-field="SysStartTime"
        caption="Changed On"
        data-type="datetime"
        format="MM/dd/yyyy HH:mm:ss"
        :width="150"
      />
      <DxColumn
        data-field="SysEndTime"
        caption="Valid Until"
        data-type="datetime"
        format="MM/dd/yyyy HH:mm:ss"
        :width="150"
        cell-template="end-time-template"
      />

      <!-- Order Fields -->
      <DxColumn
        data-field="CustomerName"
        caption="Customer"
        :width="200"
      />
      <DxColumn
        data-field="WorksheetName"
        caption="Worksheet"
        :width="150"
      />
      <DxColumn
        data-field="OrderStatusId"
        caption="Status"
        :width="120"
        :calculate-cell-value="getStatusText"
      />
      <DxColumn
        data-field="OrderTypeId"
        caption="Order Type"
        :width="120"
        :calculate-cell-value="getOrderTypeText"
      />
      <DxColumn
        data-field="SalesRepName"
        caption="Order Owner"
        :width="150"
      />
      <DxColumn
        data-field="AdditionalRepName"
        caption="Additional Rep 1"
        :width="150"
      />
      <DxColumn
        data-field="AdditionalRep2Name"
        caption="Additional Rep 2"
        :width="150"
      />
      <DxColumn
        data-field="EquipmentRepName"
        caption="Sales Rep"
        :width="150"
      />
      <DxColumn
        data-field="TransferRepName"
        caption="Transferred From"
        :width="150"
      />
      <DxColumn
        data-field="Total"
        caption="Total"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="Subtotal"
        caption="Subtotal"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="Freight"
        caption="Freight"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="SalesTax"
        caption="Sales Tax"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="SalesTaxRate"
        caption="Tax Rate %"
        data-type="number"
        :width="100"
      />
      <DxColumn
        data-field="DownPayment"
        caption="Down Payment"
        data-type="number"
        format="currency"
        :width="120"
      />
      <DxColumn
        data-field="Discount"
        caption="Discount"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="DealerCost"
        caption="Dealer Cost"
        data-type="number"
        format="currency"
        :width="120"
      />
      <DxColumn
        data-field="ActualGP"
        caption="Actual GP"
        data-type="number"
        format="currency"
        :width="100"
      />
      <DxColumn
        data-field="JdeNumber"
        caption="JDE #"
        :width="120"
      />
      <DxColumn
        data-field="BillingInfo"
        caption="Billing Info"
        :width="200"
      />
      <DxColumn
        data-field="ShippingTo"
        caption="Shipping To"
        :width="200"
      />
      <DxColumn
        data-field="InstallDate"
        caption="Install Date"
        data-type="date"
        format="MM/dd/yyyy"
        :width="120"
      />
      <DxColumn
        data-field="RevisionDate"
        caption="Revision Date"
        data-type="date"
        format="MM/dd/yyyy"
        :width="120"
      />
      <DxColumn
        data-field="ExpirationDate"
        caption="Expiration Date"
        data-type="date"
        format="MM/dd/yyyy"
        :width="120"
      />
      <DxColumn
        data-field="CommittedDate"
        caption="Committed Date"
        data-type="date"
        format="MM/dd/yyyy"
        :width="120"
      />
      <DxColumn
        data-field="SubmittedDate"
        caption="Submitted Date"
        data-type="date"
        format="MM/dd/yyyy"
        :width="120"
      />
      <DxColumn
        data-field="GeneralNote"
        caption="General Notes"
        :width="200"
        cell-template="general-note-template"
      />
      <DxColumn
        data-field="CustomFreight"
        caption="Custom Freight"
        data-type="boolean"
        :width="120"
      />
      <DxColumn
        data-field="CustomDownPayment"
        caption="Custom Down Payment"
        data-type="boolean"
        :width="150"
      />
      <DxColumn
        data-field="FreightPercentage"
        caption="Freight %"
        data-type="number"
        :width="100"
      />
      <DxColumn
        data-field="DownPaymentPercentage"
        caption="Down Payment %"
        data-type="number"
        :width="130"
      />

      <!-- Custom Templates -->
      <template #version-template="{ data }">
        <div :class="{ 'current-version': data.data.IsCurrent }">
          {{ data.data.VersionNumber }}
          <span v-if="data.data.IsCurrent" class="current-badge">CURRENT</span>
        </div>
      </template>

      <template #end-time-template="{ data }">
        <div>
          {{ data.data.IsCurrent ? 'Current' : formatDateTime(data.data.SysEndTime) }}
        </div>
      </template>

      <template #actions-template="{ data }">
        <button
          v-if="!data.data.IsCurrent && data.data.HasCompleteTemporalData"
          class="btn btn-sm btn-outline-primary"
          @click="viewHistoricalVersion(data.data)"
        >
          View
        </button>
      </template>

      <template #general-note-template="{ data }">
        <div class="note-cell" :title="data.data.GeneralNote">
          {{ truncateText(data.data.GeneralNote, 50) }}
        </div>
      </template>

    </DxDataGrid>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxScrolling,
  DxHeaderFilter,
  DxFilterRow
} from 'devextreme-vue/data-grid'
import { DxButton } from 'devextreme-vue/button'
import ordersService from '@/services/orders'
import dealerService from '@/services/dealers'
import { userRoleEnum, getKeyByValue } from '@/modules/helpers'
import { mapState } from 'vuex'

export default {
  name: 'OrderVersions',
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxScrolling,
    DxHeaderFilter,
    DxFilterRow,
    DxButton
  },
  data() {
    return {
      versions: [],
      orderNumber: '',
      orderId: null,
      customerId: null,
      changedFieldsByVersion: new Map(),
      orderStatusMap: {
        '1': 'Uncommitted',
        '2': 'Committed',
        '3': 'Submitted',
        '4': 'Installed',
        '5': 'Deleted'
      },
      orderTypeMap: {}
    }
  },
  computed: {
    ...mapState(['currentUser']),
    currentUserRole() {
      return getKeyByValue(userRoleEnum, this.currentUser.UserRoleId)
    }
  },
  created() {
    if (!['SmartSales Admin', 'Sales Administrator'].includes(this.currentUserRole)) {
      this.$router.push({ name: 'DashboardHome' })
    }
  },
  mounted() {
    this.loadOrderTypes()
    this.loadVersionHistory()
  },
  methods: {
    async loadVersionHistory() {
      const orderId = this.$route.params.id
      if (!orderId) {
        this.$store.dispatch('ADD_NOTIFICATION', { text: 'Order ID is required', type: 'error' })
        this.goBack()
        return
      }

      try {
        const response = await ordersService.getVersionHistory(orderId)

        // The response is the array itself, not wrapped in .data
        this.versions = Array.isArray(response) ? response : (response.data || [])

        if (this.versions.length > 0) {
          this.orderNumber = this.versions[0].OrderNumber
          this.orderId = this.versions[0].Id
          this.customerId = this.versions[0].CustomerId
          this.calculateChangedFields()
        }
      } catch (error) {
        console.error('Error loading version history:', error)
        console.error('Error response:', error.response)
        console.error('Error response data:', error.response?.data)

        const errorMsg = error.response?.data?.Message || error.response?.data || error.message || 'Unknown error'
        this.$store.dispatch('ADD_NOTIFICATION', {
          text: `Error loading version history: ${errorMsg}`,
          type: 'error'
        })
      }
    },
    calculateChangedFields() {
      // Compare each version with the previous one to identify changed fields
      const fieldsToCompare = [
        'CustomerName', 'JdeNumber', 'BillingInfo', 'ShippingTo', 'WorksheetName',
        'InstallDate', 'RevisionDate', 'ExpirationDate', 'CommittedDate', 'SubmittedDate',
        'Subtotal', 'Freight', 'SalesTax', 'SalesTaxRate', 'Total', 'DownPayment',
        'Discount', 'DealerCost', 'ActualGP', 'OrderStatusId', 'OrderTypeId', 'GeneralNote',
        'CustomFreight', 'CustomDownPayment', 'FreightPercentage',
        'DownPaymentPercentage', 'SalesRepName', 'AdditionalRepName', 'AdditionalRep2Name',
        'EquipmentRepName', 'TransferRepName'
      ]

      for (let i = 0; i < this.versions.length; i++) {
        const currentVersion = this.versions[i]
        const olderVersion = this.versions[i + 1] // Next in array = older version (lower version number)

        if (!olderVersion) {
          // Oldest version (version 1) - no fields highlighted since there's no previous version
          this.changedFieldsByVersion.set(currentVersion.VersionNumber, new Set())
          continue
        }

        const changedFields = new Set()
        fieldsToCompare.forEach(field => {
          const currentValue = currentVersion[field]
          const olderValue = olderVersion[field]

          // Highlight if this field changed from the older version
          if (this.isValueChanged(currentValue, olderValue)) {
            changedFields.add(field)
          }
        })

        this.changedFieldsByVersion.set(currentVersion.VersionNumber, changedFields)
      }
    },
    isValueChanged(val1, val2) {
      // Handle null/undefined
      if (val1 === null || val1 === undefined) val1 = ''
      if (val2 === null || val2 === undefined) val2 = ''

      // Handle dates
      if (val1 instanceof Date && val2 instanceof Date) {
        return val1.getTime() !== val2.getTime()
      }

      // Try to parse as numbers for numeric comparison
      const num1 = parseFloat(val1)
      const num2 = parseFloat(val2)

      if (!isNaN(num1) && !isNaN(num2)) {
        // For currency values displayed as whole dollars, round to nearest dollar
        const rounded1 = Math.round(num1)
        const rounded2 = Math.round(num2)
        // Only highlight if the rounded (displayed) values are different
        return rounded1 !== rounded2
      }

      // String comparison
      return String(val1) !== String(val2)
    },
    onCellPrepared(e) {
      // Highlight cells that changed in this version
      if (e.rowType === 'data' && e.column.dataField) {
        const versionNumber = e.data.VersionNumber
        const changedFields = this.changedFieldsByVersion.get(versionNumber)

        if (changedFields && changedFields.has(e.column.dataField)) {
          e.cellElement.style.backgroundColor = '#fff4e6'
          e.cellElement.style.fontWeight = 'bold'
          e.cellElement.title = 'This field changed in this version'
        }

        // Highlight current version row
        if (e.data.IsCurrent) {
          e.cellElement.style.borderTop = '2px solid #4CAF50'
          e.cellElement.style.borderBottom = '2px solid #4CAF50'
        }
      }
    },
    formatDateTime(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    getStatusText(rowData) {
      const statusId = String(rowData.OrderStatusId)
      return this.orderStatusMap[statusId] || statusId
    },
    getOrderTypeText(rowData) {
      const typeId = rowData.OrderTypeId
      if (!typeId) return ''
      return this.orderTypeMap[typeId] || typeId
    },
    async loadOrderTypes() {
      try {
        const res = await dealerService.orderTypes()
        if (res && res.length > 0) {
          res.forEach(option => {
            this.orderTypeMap[option.Id] = option.DisplayName
          })
        }
      } catch (error) {
        console.error('Error loading order types:', error)
      }
    },
    goToOrder() {
      // CustomerId can be null for some orders, so just use orderId
      if (this.orderId) {
        this.$router.push({
          name: 'CustomerOrderEdit',
          params: { id: this.orderId, cid: this.customerId || 0 }
        })
      } else {
        this.$store.dispatch('ADD_NOTIFICATION', {
          text: 'Unable to navigate to order',
          type: 'error'
        })
      }
    },
    goBack() {
      this.$router.push({ name: 'OrderSearch' })
    },
    viewHistoricalVersion(version) {
      if (version.IsCurrent) {
        // For current version, just go to the normal edit page
        this.goToOrder()
        return
      }

      // Navigate to CustomerOrder with historical view parameters
      // Pass versionNumber instead of SysStartTime to avoid datetime precision loss
      // during the JavaScript round-trip (datetime2(7) has 100ns precision but JS Date has only ms precision)
      this.$router.push({
        name: 'CustomerOrderEdit',
        params: {
          id: this.orderId,
          cid: this.customerId || 0
        },
        query: {
          historical: 'true',
          versionNumber: version.VersionNumber,
          asOfTime: version.SysStartTime
        }
      })
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }
  }
}
</script>

<style scoped lang="scss">
.order-versions-container {
  padding: 20px;
}

.header-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
}

.header-section h2 {
  margin: 0;
  flex: 1;
}

.order-info {
  display: flex;
  gap: 10px;
}

.order-info .label {
  font-weight: bold;
}

.order-info .value {
  color: #1976D2;
}

.btn-primary {
  margin-left: auto;
  margin-right: 10px;
}

.back-button {
  margin-left: 0;
}

.versions-grid {
  height: calc(100vh - 200px);
}

.current-version {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #4CAF50;
}

.current-badge {
  background-color: #4CAF50;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
}

.note-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  position: relative;

  &:hover {
    white-space: normal;
    overflow: visible;
    position: absolute;
    background-color: #fffde7;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 8px;
    z-index: 1000;
    max-width: 400px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    word-wrap: break-word;
  }
}
</style>
